export type GigCrewLike = {
  teensRequired?: number | null;
  selectedSeatCount?: number | null;
  confirmedSeatCount?: number | null;
  spotsRemaining?: number | null;
};

const DEFAULT_TEENS_REQUIRED = 1;
const MAX_TEENS_REQUIRED = 3;

const normalizeNonNegativeInteger = (value: number | null | undefined) => (
  typeof value === "number" && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0
);

export const normalizeTeensRequired = (value: number | null | undefined): number => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return DEFAULT_TEENS_REQUIRED;
  }

  return Math.min(MAX_TEENS_REQUIRED, Math.max(DEFAULT_TEENS_REQUIRED, Math.floor(value)));
};

export const getSelectedSeatCount = (gig: GigCrewLike): number =>
  normalizeNonNegativeInteger(gig.selectedSeatCount);

export const getConfirmedSeatCount = (gig: GigCrewLike): number =>
  normalizeNonNegativeInteger(gig.confirmedSeatCount);

export const getSpotsRemaining = (gig: GigCrewLike): number => {
  if (typeof gig.spotsRemaining === "number" && Number.isFinite(gig.spotsRemaining)) {
    return Math.max(0, Math.floor(gig.spotsRemaining));
  }

  return Math.max(0, normalizeTeensRequired(gig.teensRequired) - getSelectedSeatCount(gig) - getConfirmedSeatCount(gig));
};

export const formatTeensNeeded = (teensRequired: number | null | undefined): string => {
  const normalized = normalizeTeensRequired(teensRequired);
  return normalized === 1 ? "1 teen needed" : `${normalized} teens needed`;
};

export const formatSpotsRemaining = (spotsRemaining: number | null | undefined): string => {
  const normalized = normalizeNonNegativeInteger(spotsRemaining);
  return normalized === 1 ? "1 spot open" : `${normalized} spots open`;
};

export const formatGigCrewSummary = (gig: GigCrewLike): string =>
  `${formatTeensNeeded(gig.teensRequired)} • ${formatSpotsRemaining(getSpotsRemaining(gig))}`;
